import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useRequestData } from 'wsm-common-data';
import { setClassNames } from '@ddc/classnames-js';
import TilesSection from '../tiles/TilesSection';
import MapSection from '../map/MapSection';
import usePaginationScroll from '../../hooks/usePaginationScroll';
import { useShowFilterEvent } from '../../hooks/useShowFilterEvent';
import { usePaginationContext } from '../../contexts/PaginationContext';
import {
	DEFAULT_MAP_ZOOM,
	DEFAULT_MAP_CENTER
} from '../../constants/mapConstants';
import {
	tileDataShape,
	coordinatesShape,
	locationShape
} from '../../constants/propTypeShapes';
import '../../global-sass/main.scss';

const LocationLayout = ({
	tileData,
	showMap,
	coordinates,
	currentPageLocations,
	isLoading = false
}) => {
	const { currentPage } = usePaginationContext();
	const { deviceType } = useRequestData();
	const [hoveredTileIndex, setHoveredTileIndex] = useState(-1);
	const isMobile = deviceType === 'MOBILE';
	const { scrollTargetRef } = usePaginationScroll(currentPage, isMobile);
	const showFilter = useShowFilterEvent();
	const shouldShowMap = showMap && !isMobile;

	// Memoize map locations to keep a stable reference and prevent
	// the markers effect from re-running and resetting zoom/center
	const mapLocations = useMemo(
		() =>
			currentPageLocations.length > 0 ? currentPageLocations : tileData,
		[currentPageLocations, tileData]
	);

	useEffect(() => {
		setHoveredTileIndex(-1); // Reset hovered tile on page change
	}, [currentPage]);

	// Determine map center: use coordinates from search, or first location, or default
	const firstLocation = tileData[0]?.location || tileData[0];
	/* eslint-disable react-hooks/exhaustive-deps -- deps use sub-properties (lat/lng) to avoid re-renders on object reference changes */
	const mapCenter = useMemo(() => {
		if (coordinates) return coordinates;
		if (firstLocation?.latitude && firstLocation?.longitude) {
			return {
				lat: firstLocation.latitude,
				lng: firstLocation.longitude
			};
		}
		return DEFAULT_MAP_CENTER;
	}, [
		coordinates?.lat,
		coordinates?.lng,
		firstLocation?.latitude,
		firstLocation?.longitude
	]);
	/* eslint-enable react-hooks/exhaustive-deps */

	const containerClassNames = setClassNames([
		'location-layout',
		'mx-auto',
		'py-4',
		'py-lg-5',
		showFilter ? 'pl-4' : ''
	]);

	const tilesClassNames = setClassNames([
		'flex-fill',
		'mb-4',
		'pr-0',
		shouldShowMap ? 'pr-7 pr-xl-8' : ''
	]);

	return (
		<div className={containerClassNames} data-testid="location-layout">
			<div className="d-flex flex-column flex-lg-row w-100">
				<div
					className={tilesClassNames}
					style={{ minWidth: 0 }}
					ref={scrollTargetRef}
				>
					<TilesSection
						tileData={tileData}
						onTileHover={setHoveredTileIndex}
					/>
				</div>

				{shouldShowMap && (
					<MapSection
						center={mapCenter}
						zoom={DEFAULT_MAP_ZOOM}
						locations={mapLocations}
						hoveredTileIndex={hoveredTileIndex}
						isLoading={isLoading}
					/>
				)}
			</div>
		</div>
	);
};

LocationLayout.propTypes = {
	tileData: PropTypes.arrayOf(tileDataShape).isRequired,
	showMap: PropTypes.bool.isRequired,
	coordinates: coordinatesShape,
	currentPageLocations: PropTypes.arrayOf(
		PropTypes.shape({
			location: locationShape
		})
	),
	isLoading: PropTypes.bool
};

LocationLayout.defaultProps = {
	coordinates: undefined,
	currentPageLocations: []
};

export default LocationLayout;
